<script setup lang="ts">
import { computed, onMounted, useTemplateRef } from "vue";

import BaseInput from "@/components/ui/BaseInput/BaseInput.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import ScreenReaderMessage from "@/components/ui/a11y/ScreenReaderMessage.vue";
import { useNativeEvents } from "@/composables/useNativeEvents";
import { firedWithinElement } from "@/utils/dom/dom";

defineOptions({ inheritAttrs: false });

const modelValue = defineModel<string>({ required: true });

interface Props {
  iconPosition?: "before" | "after"; // Position of the search icon
  iconTitle?: string; // Used as label for the input, unless there's a visual label (attribute 'label')
  autofocus?: boolean;
  placeholder?: string;
  resultsCount?: number; // For screen readers
  setReturnFocus?: string;
  size?: "small" | "medium";
  wrapperClass?: string;
}

const props = defineProps<Props>();

const searchRef = useTemplateRef("search");

const { addEventListener } = useNativeEvents();

const hasText = computed(() => modelValue.value.length > 0);

onMounted(() => {
  addEventListener(document, "keydown", keydown, { capture: true });
});

function clearInput() {
  modelValue.value = "";
  // Keep focus on the input
  const searchInput = searchRef.value?.querySelector("input");
  searchInput?.focus();
}

function keydown(e: KeyboardEvent) {
  if (e.key === "Escape") {
    if (firedWithinElement(e, searchRef.value)) {
      const searchInput = searchRef.value!.querySelector("input");

      // First Escape: clear the input if there's text
      if (modelValue.value.length > 0) {
        modelValue.value = "";
        e.stopImmediatePropagation();
      } else {
        // Second Escape (or first if no text): blur and return focus
        searchInput?.blur();
        if (props.setReturnFocus) {
          document.getElementById(props.setReturnFocus)?.focus();
          e.stopImmediatePropagation();
        }
      }
    }
  }
}
</script>

<template>
  <div ref="search" class="search-input-wrapper" :class="[wrapperClass]">
    <BaseInput
      v-model="modelValue"
      v-autofocus="!!autofocus"
      v-bind="$attrs"
      control-type="text"
      :icon-after="iconPosition === 'after' ? 'general/search' : undefined"
      :icon-after-title="iconTitle"
      :icon-before="iconPosition === 'before' ? 'general/search' : undefined"
      :icon-before-title="iconTitle"
      :placeholder="placeholder || $t('action.search')"
      :size
      maxlength="100"
      :wrapper-class="['search-input', size, { 'has-clear-button': hasText }]"
    />
    <SvgIcon
      v-if="hasText"
      name="system/close"
      class="clear-button"
      :class="[size, { 'icon-after': iconPosition === 'after' }]"
      :title="$t('general.clear')"
      role="button"
      tabindex="0"
      width="18"
      height="18"
      @click="clearInput"
      @keydown.enter.space.prevent="clearInput"
    />
    <ScreenReaderMessage v-if="typeof resultsCount === 'number'">
      {{ $t("searchResultsTs.results", resultsCount) }}
    </ScreenReaderMessage>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";

.search-input-wrapper {
  position: relative;
  display: inline-block;
}

.clear-button {
  position: absolute;
  top: 50%;
  transform: translateY(-45%);
  cursor: pointer;
  color: colors.$icon-subtle;
  right: 8px;

  &.small {
    width: 14px;
    height: 14px;
    right: 8px;

    &.icon-after {
      right: 30px;
    }
  }

  &.medium {
    width: 20px;
    height: 20px;
    right: 12px;

    &.icon-after {
      right: 40px;
    }
  }

  &:hover {
    color: colors.$icon;
  }

  &:focus {
    outline: 2px solid colors.$border-focused;
    outline-offset: 2px;
    border-radius: 2px;
  }
}
</style>
